import type { ReactNode } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';

import { overlineSx, wf } from './shell/tokens';

export const STAFF_INTAKE_TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'verification', label: 'Verification' },
  { value: 'billing', label: 'Billing' },
  { value: 'credentialing', label: 'Credentialing' },
  { value: 'ehr-import', label: 'EHR Migration' },
] as const;
export type StaffIntakeTab = (typeof STAFF_INTAKE_TABS)[number]['value'];

/**
 * Count badges are decorative: they repeat a number the panel below already
 * states, so they stay out of the tab's accessible name.
 */
function TabLabel({ label, count, active }: { label: string; count?: number; active: boolean }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <span>{label}</span>
      {count === undefined ? null : (
        <Box
          aria-hidden
          component="span"
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            lineHeight: 1.5,
            borderRadius: '999px',
            padding: '2px 7px',
            backgroundColor: active ? wf.blue : '#eeeef6',
            color: active ? wf.surface : wf.muted,
          }}
        >
          {count}
        </Box>
      )}
    </Box>
  );
}

export function StaffIntakeStatusShell({
  selected,
  onSelect,
  counts,
  children,
}: {
  selected: StaffIntakeTab;
  onSelect: (tab: StaffIntakeTab) => void;
  /** Only the tabs whose count the page already has in hand; others render bare. */
  counts?: Partial<Record<StaffIntakeTab, number>>;
  children: ReactNode;
}) {
  return (
    <Box sx={{ bgcolor: wf.page, minHeight: '100%' }}>
      <Box sx={{ maxWidth: 1180, mx: 'auto', px: { xs: 2, md: '22px' }, pt: '26px', pb: '100px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '22px',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={overlineSx}>Internal · Intake Hub</Typography>
            <Typography
              component="h1"
              sx={{
                m: '6px 0 0',
                fontSize: '22px',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: wf.ink,
              }}
            >
              Intake status
            </Typography>
            <Typography
              sx={{
                fontSize: '13px',
                color: wf.muted,
                lineHeight: 1.6,
                mt: '5px',
                maxWidth: 680,
              }}
            >
              Review practice intake and setup status. Open an organization’s Intake Hub to
              continue.
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: '12px',
              color: wf.meta,
              lineHeight: 1.5,
              textAlign: { xs: 'left', md: 'right' },
              maxWidth: 260,
            }}
          >
            Billing reads from Payer Connections. Credentialing and EHR migration are recorded in
            each organization’s Intake Hub.
          </Typography>
        </Box>
        <Tabs
          value={selected}
          onChange={(_event, value: StaffIntakeTab) => onSelect(value)}
          variant="scrollable"
          aria-label="Staff intake tracks"
          sx={{
            mt: '20px',
            mb: '20px',
            minHeight: 0,
            borderBottom: `1px solid ${wf.line}`,
            '& .MuiTabs-indicator': { height: '3px', backgroundColor: wf.blue },
            '& .MuiTab-root': {
              minHeight: 0,
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              color: wf.muted,
              padding: '10px 14px 12px',
            },
            '& .MuiTab-root.Mui-selected': { fontWeight: 700, color: wf.ink },
          }}
        >
          {STAFF_INTAKE_TABS.map(tab => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={
                <TabLabel
                  label={tab.label}
                  count={counts?.[tab.value]}
                  active={selected === tab.value}
                />
              }
              id={`intake-tab-${tab.value}`}
              aria-controls={`intake-panel-${tab.value}`}
            />
          ))}
        </Tabs>
        <Box
          role="tabpanel"
          id={`intake-panel-${selected}`}
          aria-labelledby={`intake-tab-${selected}`}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
